import React, { FC, useCallback, useMemo, useState } from 'react';
import { IonContent, IonPage, useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { ChevronHeader } from '../../../components/ChevronHeader';
import { CancelSession, Container, ReScheduleSuccess } from '@mindly/ui-components';
import { setIsActive } from '../../../redux/tabBar/reducer';
import { PrivateRoutes, PublicRoutes } from '../../../routes/navigation';
import { RouteComponentProps, useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { consultationsByIDSelector } from '../../../redux/consultations/selector';
import { consultationTimeToFormatSeconds } from '../../SchedulePage/utils';
import { cancelConsultationByID } from '../../../components/FutureConsultationCard/utils';
import { deleteById } from '../../../redux/consultations/reducer';
import { globalVariables } from '../../../redux/firestoreGlobalVariables/selector';
import { CancelSessionWrapper } from './CancelSessionConfirmPage.style';
import { fetchSubscriptions } from '../../../redux/subscriptions/thunk';
import { getUsersPsychologistsListThunk } from '../../../redux/usersPsychologists/action';
import { DateTime } from 'luxon';
import { getChatRoomsListBySpecialistId } from '../../../api/agora/chatRooms';
import { ChatRoomsData } from '../../../redux/chatRooms/types';
import { SystemMessagesType, UserType } from '../../types';
import useAgoraRtm from '../../../context/AgoraContext/AgoraContext';
import { userSelector } from '../../../redux/user/selector';
import useLangs from '../../../context/LangsContext';
import { useIntl } from 'react-intl';
import { formatSystemMessage } from '../../../context/AgoraContext/formatSystemMessage';
import EmailSystem from '../../../api/emails';
import { psychologistSelectorById } from '../../../redux/specialists/selector';
import useToast from '../../../context/ToastContext/ToastContext';
import { fetchPromoCodes } from '../../../redux/promoCodes/promoCodes.thunk';
import { addDeletedSessionId } from '../../../redux/consultations/deletedSessions.reducer';
import { useAnalytics, useAuthFlow } from 'src/shared';

interface CancelSessionConfirmPageProps
  extends RouteComponentProps<{
    sessionID: string;
  }> {}

const CancelSessionConfirmPage: FC<CancelSessionConfirmPageProps> = ({ match }) => {
  const history = useHistory();
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const { locale } = useLangs();
  const intl = useIntl();

  const { trackPredefinedEvent } = useAnalytics();
  const { authUser } = useAuthFlow();

  const { sessionID } = match.params;

  const { sendSystemMessage } = useAgoraRtm();

  const sessionData = useSelector(consultationsByIDSelector(sessionID));
  const currentSpecialist = useSelector(psychologistSelectorById(sessionData?.psychologist_id ?? ''));
  const globalVars = useSelector(globalVariables);
  const currentUser = useSelector(userSelector);

  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useIonViewWillEnter(() => {
    if (!currentUser?.id) {
      history.push(PublicRoutes.AUTH);
      return;
    }

    dispatch(setIsActive(false));
    if (!sessionData) {
      history.push(PrivateRoutes.CONSULTATION);
    }
  });

  useIonViewWillLeave(() => {
    setIsSuccess(false);
  });

  const specialistName = useMemo(
    () => `${sessionData?.psychologist_name.split(' ')[0]} ${sessionData?.psychologist_name.split(' ')[1][0]}.`,
    [sessionData]
  );

  const handleCancelSession = useCallback(
    async (reason: string) => {
      if (!sessionData || !authUser?.uid) return;

      try {
        setIsLoading(true);
        const cancelConsultationResponse = await cancelConsultationByID(
          sessionData.id,
          globalVars.minimumPeriodForCancellationSeconds,
          consultationTimeToFormatSeconds(sessionData.consultation_time),
          intl,
          authUser?.uid,
          sessionData?.subscription_id,
          sessionData?.payment_intent_id
        );

        if (cancelConsultationResponse?.error) {
          return showToast({ error: cancelConsultationResponse.error });
        }

        dispatch(addDeletedSessionId(sessionData.id));

        await Promise.all([
          await dispatch(fetchSubscriptions(sessionData.user_id)).unwrap(),
          await dispatch(getUsersPsychologistsListThunk()),
        ]);

        const { data: roomData } = await getChatRoomsListBySpecialistId(
          sessionData.user_id,
          sessionData.psychologist_id
        );

        await sendSystemMessage({
          roomData: roomData?.[0] as ChatRoomsData,
          specialistId: sessionData.psychologist_id,
          user: currentUser as Omit<UserType, 'id'> & { id: string },
          message: formatSystemMessage(
            SystemMessagesType.session_canceled,
            {
              display_name: currentUser?.display_name ?? '',
              reason,
              consultation_time: sessionData.consultation_time,
            },
            intl
          ),
          notifyMessage: `${intl.formatMessage({
            id: 'booking.notify_message.cancel',
            defaultMessage: 'Скасував(ла) сеанс',
          })} ${DateTime.fromSeconds(consultationTimeToFormatSeconds(sessionData.consultation_time))
            .setZone(currentSpecialist?.time_zone ?? 'UTC')
            .setLocale(locale)
            .toFormat('dd MMMM HH:mm')}`,
          locale,
        });
        EmailSystem.sendSessionRescheduledOrCanceled('cancel')(sessionID, reason);

        trackPredefinedEvent('refund', {
          currency: 'EUR',
          value: sessionData.price_default,
        });

        dispatch(deleteById(sessionData.id));
        await dispatch(fetchPromoCodes({ client_id: sessionData.user_id }));

        setIsSuccess(true);
      } catch (error: any) {
        showToast({ error });
      } finally {
        setIsLoading(false);
      }
    },
    [sessionData, globalVars, currentSpecialist?.time_zone]
  );

  return (
    <IonPage>
      <ChevronHeader />
      {isSuccess ? (
        <ReScheduleSuccess
          name={specialistName}
          avatar={sessionData?.psychologist_avatar ?? ''}
          time={consultationTimeToFormatSeconds(sessionData?.consultation_time ?? '')}
          timeZone={DateTime.local().zoneName}
          onClick={() => {
            history.push(PrivateRoutes.CONSULTATION);
          }}
          type="cancel"
          locale={locale}
          success={intl.formatMessage({
            id: 'general.btn.success',
            defaultMessage: 'Готово',
          })}
          cancelTitle={intl.formatMessage({
            id: 'booking.cancel.title',
            defaultMessage: 'Сеанс скасовано',
          })}
          description={intl.formatMessage({
            id: 'booking.cancel.description',
            defaultMessage: 'Твої цілі чекають на тебе. Заплануй новий сеанс, щоб продовжувати терапію.',
          })}
          successTitle={intl.formatMessage({
            id: 'booking.reschedule.title',
            defaultMessage: 'Сеанс перенесено на',
          })}
        />
      ) : (
        <IonContent>
          <Container>
            {sessionData && (
              <CancelSessionWrapper>
                <CancelSession
                  name={specialistName}
                  avatar={sessionData.psychologist_avatar}
                  time={consultationTimeToFormatSeconds(sessionData.consultation_time)}
                  timeZone={DateTime.local().zoneName}
                  emptyReasonErrorMessage={intl.formatMessage({
                    id: 'booking.cancel_session.emptyReasonErrorMessage',
                    defaultMessage: 'Щоб продовжити, будь ласка, надайте коментар.',
                  })}
                  onReschedule={() => {
                    history.push(
                      PrivateRoutes.BOOKING_SYSTEM.replace(':id', sessionData.psychologist_id).replace(
                        ':consultationID?',
                        sessionData?.id
                      )
                    );
                  }}
                  onSubmit={handleCancelSession}
                  isLoading={isLoading}
                  locale={locale}
                  translations={{
                    title: intl.formatMessage(
                      {
                        id: 'session.cancel.confirm.title',
                        defaultMessage: 'Поясни своєму {who} чому тобі потрібно скасувати сеанс',
                      },
                      {
                        who: intl.formatMessage({
                          id: 'general.specialist',
                          defaultMessage: 'психологу',
                        }),
                      }
                    ),
                    placeholder: intl.formatMessage({
                      id: 'session.cancel.confirm.placeholder',
                      defaultMessage: 'Мені потрібно скасувати сеанс, тому що...',
                    }),
                    cancelBtnLabel: intl.formatMessage({
                      id: 'session.cancel.btn',
                      defaultMessage: 'Скасувати сеанс',
                    }),
                    rescheduleBtnLabel: intl.formatMessage({
                      id: 'session.reschedule.btn',
                      defaultMessage: 'Перенести сеанс',
                    }),
                    question: intl.formatMessage({
                      id: 'session.cancel.question',
                      defaultMessage: 'Хочеш скасувати сеанс?',
                    }),
                    description: intl.formatMessage({
                      id: 'session.cancel.description',
                      defaultMessage: 'Щоб змінити дату або час сеансу, не скасовуй його, просто перенеси сеанс.',
                    }),
                  }}
                />
              </CancelSessionWrapper>
            )}
          </Container>
        </IonContent>
      )}
    </IonPage>
  );
};

export default CancelSessionConfirmPage;
